import React, { useMemo, useState } from 'react';

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

const asArray = (v) => (Array.isArray(v) ? v : []);

const nonEmptyString = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');
const isNonEmptyObject = (v) => isPlainObject(v) && Object.keys(v).length > 0;

const looksLikeUserContext = (obj) => {
  if (!isPlainObject(obj)) return false;
  // Heuristic: common keys we expect in "user_context"
  const keys = ['tools', 'skills', 'constraints', 'knowledge_gaps', 'known_facts'];
  return keys.some((k) => k in obj);
};

const pickViewerUserContext = (worldState, viewerId) => {
  if (!isPlainObject(worldState)) return null;

  // Common shapes we might see (historical or future):
  // 1) world_state.user_context = { tools: [], skills: [], ... } (already viewer-specific)
  // 2) world_state.user_context = { [userId]: { tools: [], ... }, ... } (per-user map)
  // 3) world_state.user_contexts / user_context_by_user / by_user = { [userId]: { ... } }
  const candidates = [
    worldState.user_context,
    worldState.user_contexts,
    worldState.user_context_by_user,
    worldState.by_user,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (looksLikeUserContext(candidate)) {
      return candidate;
    }

    if (viewerId && isPlainObject(candidate) && candidate[viewerId]) {
      const maybe = candidate[viewerId];
      if (looksLikeUserContext(maybe)) return maybe;
    }
  }

  return null;
};

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
    {children}
  </span>
);

const SectionTitle = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
    {children}
  </div>
);

const RelationshipTable = ({ relationships, limit = 12 }) => {
  const [showAll, setShowAll] = useState(false);
  const rows = asArray(relationships).filter(Boolean);
  const visible = showAll ? rows : rows.slice(0, limit);

  if (rows.length === 0) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">No relationships yet.</div>;
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-slate-800/60">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Source
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Relation
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Target
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-slate-900/30">
            {visible.map((rel, idx) => (
              <tr key={`${rel?.source || 's'}:${rel?.relation || 'r'}:${rel?.target || 't'}:${idx}`}>
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                  {rel?.source || '—'}
                </td>
                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{rel?.relation || '—'}</td>
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                  {rel?.target || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > limit && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="text-xs font-semibold text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          type="button"
        >
          {showAll ? 'Show less' : `Show all (${rows.length})`}
        </button>
      )}
    </div>
  );
};

export default function WorldModelDebugPanel({ worldState, viewer, viewerContext }) {
  const viewerId = useMemo(() => {
    const raw = viewer?.id || viewer?._id;
    return raw ? String(raw) : '';
  }, [viewer?.id, viewer?._id]);

  const snapshot = useMemo(() => (isPlainObject(worldState) ? worldState : {}), [worldState]);

  const narrativeSummary = useMemo(
    () => nonEmptyString(snapshot?.narrative_summary),
    [snapshot?.narrative_summary]
  );

  const scaffoldPlan = useMemo(
    () => (isPlainObject(snapshot?.scaffold_plan) ? snapshot.scaffold_plan : null),
    [snapshot?.scaffold_plan]
  );

  const relationships = useMemo(() => asArray(snapshot?.relationships), [snapshot?.relationships]);
  const knownFacts = useMemo(() => asArray(snapshot?.known_facts), [snapshot?.known_facts]);

  const viewerUserContext = useMemo(() => {
    if (isPlainObject(viewerContext)) return viewerContext;
    return pickViewerUserContext(snapshot, viewerId);
  }, [viewerContext, snapshot, viewerId]);

  const hasAnyData =
    Boolean(narrativeSummary) ||
    isNonEmptyObject(scaffoldPlan) ||
    relationships.length > 0 ||
    knownFacts.length > 0 ||
    Boolean(viewerUserContext);

  if (!hasAnyData) {
    return (
      <details className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
        <summary className="text-sm font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
        Assistant Memory
        </summary>
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          No world model snapshot available yet.
        </div>
      </details>
    );
  }

  return (
    <details className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
      <summary className="text-sm font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
        Assistant Memory
      </summary>

      <div className="mt-3 space-y-5">
        <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          A compact view of what the assistant thinks is going on. This can be incomplete or wrong.
        </div>

        {narrativeSummary && (
          <div className="space-y-2">
            <SectionTitle>Narrative summary</SectionTitle>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900/30 px-3 py-2">
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                {narrativeSummary}
              </p>
            </div>
          </div>
        )}

        {isNonEmptyObject(scaffoldPlan) && (
          <div className="space-y-2">
            <SectionTitle>Scaffold plan</SectionTitle>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900/30 px-3 py-2 space-y-2">
              <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                <div className="font-medium text-gray-900 dark:text-white">
                  {(scaffoldPlan?.thread_objective_name || 'Current thread') + ''}
                </div>
                {(nonEmptyString(scaffoldPlan?.focus_source) || nonEmptyString(scaffoldPlan?.focus_success_criterion)) && (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {nonEmptyString(scaffoldPlan?.focus_source) && (
                      <Chip>focus_source: {String(scaffoldPlan.focus_source)}</Chip>
                    )}
                    {nonEmptyString(scaffoldPlan?.focus_success_criterion) && (
                      <Chip>success: {String(scaffoldPlan.focus_success_criterion)}</Chip>
                    )}
                  </div>
                )}
                {isPlainObject(scaffoldPlan?.turn_policy) && (
                  <div className="mt-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Turn policy
                    </span>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {nonEmptyString(scaffoldPlan?.turn_policy?.budget_level) && (
                        <Chip>budget: {String(scaffoldPlan.turn_policy.budget_level)}</Chip>
                      )}
                      {scaffoldPlan?.turn_policy?.max_questions != null && (
                        <Chip>max_q: {String(scaffoldPlan.turn_policy.max_questions)}</Chip>
                      )}
                      {scaffoldPlan?.turn_policy?.max_options != null && (
                        <Chip>max_opts: {String(scaffoldPlan.turn_policy.max_options)}</Chip>
                      )}
                      {scaffoldPlan?.turn_policy?.max_evidence_tier != null && (
                        <Chip>max_tier: {String(scaffoldPlan.turn_policy.max_evidence_tier)}</Chip>
                      )}
                      {scaffoldPlan?.turn_policy?.allow_forms != null && (
                        <Chip>forms: {String(scaffoldPlan.turn_policy.allow_forms)}</Chip>
                      )}
                      {scaffoldPlan?.turn_policy?.allow_learn_sidecar != null && (
                        <Chip>learn: {String(scaffoldPlan.turn_policy.allow_learn_sidecar)}</Chip>
                      )}
                    </div>
                    {nonEmptyString(scaffoldPlan?.turn_policy?.reason) && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                        {String(scaffoldPlan.turn_policy.reason)}
                      </div>
                    )}
                  </div>
                )}
                {isPlainObject(scaffoldPlan?.last_switch) && (
                  <div className="mt-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Last switch
                    </span>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 space-y-1">
                      {nonEmptyString(scaffoldPlan?.last_switch?.trigger) && (
                        <div>trigger: {String(scaffoldPlan.last_switch.trigger)}</div>
                      )}
                      {nonEmptyString(scaffoldPlan?.last_switch?.why_now) && (
                        <div>why: {String(scaffoldPlan.last_switch.why_now)}</div>
                      )}
                      {nonEmptyString(scaffoldPlan?.last_switch?.paused) && (
                        <div>paused: {String(scaffoldPlan.last_switch.paused)}</div>
                      )}
                      {nonEmptyString(scaffoldPlan?.last_switch?.success_now) && (
                        <div>success: {String(scaffoldPlan.last_switch.success_now)}</div>
                      )}
                    </div>
                  </div>
                )}
                {nonEmptyString(scaffoldPlan?.hypothesis) && (
                  <div className="mt-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Hypothesis
                    </span>
                    <div>{scaffoldPlan.hypothesis}</div>
                  </div>
                )}
                {nonEmptyString(scaffoldPlan?.last_attempt) && (
                  <div className="mt-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Last attempt
                    </span>
                    <div>{scaffoldPlan.last_attempt}</div>
                  </div>
                )}
                {(scaffoldPlan?.stuck_event || scaffoldPlan?.last_user_result || scaffoldPlan?.ladder_step_index != null) && (
                  <div className="mt-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Ladder state
                    </span>
                    <div className="mt-1 space-y-1">
                      {scaffoldPlan?.ladder_step_index != null && (
                        <div>
                          <span className="mr-2 inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
                            step {String(scaffoldPlan.ladder_step_index)}
                          </span>
                          {nonEmptyString(scaffoldPlan?.last_user_result) && (
                            <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
                              result: {String(scaffoldPlan.last_user_result)}
                            </span>
                          )}
                        </div>
                      )}
                      {isPlainObject(scaffoldPlan?.stuck_event) && (
                        <div className="text-xs text-gray-700 dark:text-gray-200">
                          <span className="mr-2 inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
                            stuck: {String(scaffoldPlan.stuck_event.active)}
                          </span>
                          {nonEmptyString(scaffoldPlan?.stuck_event?.kind) && (
                            <span className="mr-2 inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
                              {String(scaffoldPlan.stuck_event.kind)}
                            </span>
                          )}
                          {scaffoldPlan?.stuck_event?.evidence_this_turn != null && (
                            <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
                              evidence: {String(scaffoldPlan.stuck_event.evidence_this_turn)}
                            </span>
                          )}
                        </div>
                      )}
                      {Array.isArray(scaffoldPlan?.tried_steps) && scaffoldPlan.tried_steps.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {scaffoldPlan.tried_steps.slice(-8).map((s, idx) => (
                            <Chip key={`${String(s)}:${idx}`}>{String(s)}</Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {nonEmptyString(scaffoldPlan?.delta_dimension) && nonEmptyString(scaffoldPlan?.next_shift) && (
                  <div className="mt-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Next delta
                    </span>
                    <div>
                      <span className="mr-2 inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200">
                        {String(scaffoldPlan.delta_dimension)}
                      </span>
                      {scaffoldPlan.next_shift}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {viewerUserContext && (
          <div className="space-y-2">
            <SectionTitle>Your context</SectionTitle>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900/30 px-3 py-2 space-y-3">
              {asArray(viewerUserContext.tools).length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Tools
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {asArray(viewerUserContext.tools).map((t, idx) => (
                      <Chip key={`${t}:${idx}`}>{String(t)}</Chip>
                    ))}
                  </div>
                </div>
              )}

              {asArray(viewerUserContext.skills).length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Skills
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {asArray(viewerUserContext.skills).map((s, idx) => (
                      <Chip key={`${s}:${idx}`}>{String(s)}</Chip>
                    ))}
                  </div>
                </div>
              )}

              {asArray(viewerUserContext.constraints).length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Constraints
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {asArray(viewerUserContext.constraints).map((c, idx) => (
                      <Chip key={`${c}:${idx}`}>{String(c)}</Chip>
                    ))}
                  </div>
                </div>
              )}

              {asArray(viewerUserContext.knowledge_gaps).length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Knowledge gaps
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {asArray(viewerUserContext.knowledge_gaps).map((g, idx) => (
                      <Chip key={`${g}:${idx}`}>{String(g)}</Chip>
                    ))}
                  </div>
                </div>
              )}

              {asArray(viewerUserContext.known_facts).length > 0 && (
                <div className="space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Personal facts
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700 dark:text-gray-200">
                    {asArray(viewerUserContext.known_facts).map((f, idx) => (
                      <li key={`${f}:${idx}`}>{String(f)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {Object.keys(viewerUserContext).length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">No viewer context.</div>
              )}
            </div>
          </div>
        )}

        {knownFacts.length > 0 && (
          <div className="space-y-2">
            <SectionTitle>Shared facts</SectionTitle>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900/30 px-3 py-2">
              <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700 dark:text-gray-200">
                {knownFacts.slice(0, 20).map((f, idx) => (
                  <li key={`${f}:${idx}`}>{String(f)}</li>
                ))}
              </ul>
              {knownFacts.length > 20 && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Showing 20 of {knownFacts.length}.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <SectionTitle>Relationships</SectionTitle>
          <RelationshipTable relationships={relationships} />
        </div>
      </div>
    </details>
  );
}


